import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import {
  AuditSummaryCards,
  AuditFilters,
  AuditTable,
  AuditDetailsDialog,
  useContentCheckAudit,
} from './ContentCheckAuditDashboardComponents';

/**
 * Content Check Audit Dashboard Component
 *
 * Displays comprehensive content check audit data ordered by timestamp (descending).
 * Provides detailed tracking of all content checks across templates and executions.
 */
const ContentCheckAuditDashboard = () => {
  const {
    auditLogs,
    loading,
    error,
    selectedLog,
    detailsOpen,
    page,
    pageSize,
    totalPages,
    totalCount,
    searchTerm,
    referenceTypeFilter,
    severityFilter,
    userFilter,
    userFilterAnchor,
    sortBy,
    sortOrder,
    uniqueUsers,
    sortedAuditLogs,
    setSearchTerm,
    setReferenceTypeFilter,
    setSeverityFilter,
    setPageSize,
    setPage,
    setDetailsOpen,
    fetchAuditLogs,
    handleViewDetails,
    handleExport,
    handleSort,
    handleUserFilterOpen,
    handleUserFilterClose,
    handleUserFilterSelect,
  } = useContentCheckAudit();

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="h4" gutterBottom>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Content Check Audit Dashboard
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Comprehensive tracking of all content checks and anonymizations across
        templates and executions
      </Typography>

      <AuditSummaryCards auditLogs={auditLogs} totalCount={totalCount} />

      <AuditFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        referenceTypeFilter={referenceTypeFilter}
        onReferenceTypeChange={setReferenceTypeFilter}
        severityFilter={severityFilter}
        onSeverityChange={setSeverityFilter}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onExport={handleExport}
        onRefresh={fetchAuditLogs}
      />

      <AuditTable
        auditLogs={sortedAuditLogs}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        userFilter={userFilter}
        uniqueUsers={uniqueUsers}
        userFilterAnchor={userFilterAnchor}
        onUserFilterOpen={handleUserFilterOpen}
        onUserFilterClose={handleUserFilterClose}
        onUserFilterSelect={handleUserFilterSelect}
        onViewDetails={handleViewDetails}
        page={page}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <AuditDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        selectedLog={selectedLog}
      />
    </Box>
  );
};

export default ContentCheckAuditDashboard;
